import {
  AlertTriangle as AlertIcon,
  ArrowLeft as ArrowLeftIcon,
  CheckCircle2 as CheckCircleIcon,
  FileText as FileTextIcon,
  SendHorizontal as SendIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type {
  WhatsAppSelectedPhoneNumber,
  WhatsAppTemplateDto,
  WhatsAppTemplatePresentation,
} from "@multi-chat/shared";
import { LinkifiedText } from "../../../components/common/LinkifiedText/index.js";
import { Button } from "../../../components/ui/button/index.js";
import { cn } from "../../../lib/utils.js";
import {
  senderDisplayName,
  splitTemplatePreviewSegments,
  type TemplatePreviewSegment,
} from "../../campaigns/campaign-message-presentation.js";
import { CampaignPhonePreview } from "../../campaigns/CampaignPhonePreview.js";
import {
  WhatsAppTemplatePreview,
  type WhatsAppTemplatePreviewProps,
} from "../../whatsapp/WhatsAppTemplatePreview/index.js";
import { INBOX_SCROLLER_CLASS } from "../inbox-surface-classes.js";
import {
  slotStorageKey,
  templateSlotInputDir,
  type TemplateSendDiscovery,
  type TemplateSendSlot,
} from "../template-send.js";
import { TemplateMetaBadges } from "./TemplatePickerBadges.js";

/** Where the phone renders: its own picker column, or inside the detail pane. */
export type TemplatePickerPhonePlacement = "column" | "inline";

const INLINE_PHONE_FRAME_CLASS = "max-w-[10.5rem]";

const footerButtonClass = "rounded-lg px-3 text-[0.78125rem]";

/** Unresolved placeholders read amber in the phone; filled values keep a light tint so the edit is visible. */
function TemplatePreviewTokens({
  segments,
}: {
  segments: readonly TemplatePreviewSegment[];
}): React.JSX.Element {
  return (
    <>
      {segments.map((segment, index) => {
        switch (segment.kind) {
          case "text":
            return <LinkifiedText key={`text-${index}`} text={segment.text} />;
          case "variable":
            return (
              <span
                key={`var-${segment.parameterKey}-${segment.occurrence}`}
                data-placeholder={segment.mapped ? "filled" : "missing"}
                className={cn(
                  "rounded-[4px] px-0.5 font-semibold",
                  segment.mapped ? "bg-[rgb(23_126_103/0.1)]" : "bg-[#fdf0e3] text-[#8a4b12]",
                )}
              >
                {segment.mapped ? <bdi>{segment.display}</bdi> : <bdi dir="ltr">{segment.display}</bdi>}
              </span>
            );
          default: {
            const _exhaustive: never = segment;
            return _exhaustive;
          }
        }
      })}
    </>
  );
}

function templatePreviewTextSlots(
  discovery: TemplateSendDiscovery | null,
  valuesBySlotKey: ReadonlyMap<string, string>,
): WhatsAppTemplatePreviewProps["textSlots"] {
  if (discovery?.kind !== "supported") return undefined;
  const header =
    discovery.headerFormat === "TEXT" && discovery.headerText
      ? splitTemplatePreviewSegments({
          text: discovery.headerText,
          componentType: "HEADER",
          slots: discovery.slots,
          valuesBySlotKey,
        })
      : null;
  const body = discovery.bodyText?.trim()
    ? splitTemplatePreviewSegments({
        text: discovery.bodyText,
        componentType: "BODY",
        slots: discovery.slots,
        valuesBySlotKey,
      })
    : null;
  return {
    ...(header ? { header: <TemplatePreviewTokens segments={header} /> } : {}),
    ...(body ? { body: <TemplatePreviewTokens segments={body} /> } : {}),
  };
}

export function TemplatePickerPhone({
  template,
  discovery,
  preview,
  valuesBySlotKey,
  sender,
  frameClassName,
}: {
  template: WhatsAppTemplateDto | null;
  discovery: TemplateSendDiscovery | null;
  preview: WhatsAppTemplatePresentation | null;
  valuesBySlotKey: ReadonlyMap<string, string>;
  sender: WhatsAppSelectedPhoneNumber | null;
  frameClassName: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const placeholder =
    template == null
      ? t("inbox.templateSend.selectPromptTitle")
      : discovery?.kind === "unsupported"
        ? t("inbox.templateSend.unsupportedType")
        : null;

  return (
    <CampaignPhonePreview
      senderName={sender ? senderDisplayName(sender) : null}
      senderPhone={sender?.phoneNumber ?? null}
      contentKey={template ? String(template.id) : "none"}
      frameClassName={frameClassName}
      compact
    >
      {preview && template ? (
        <WhatsAppTemplatePreview
          presentation={preview}
          contentLanguage={template.language}
          textSlots={templatePreviewTextSlots(discovery, valuesBySlotKey)}
        />
      ) : placeholder ? (
        <p className="m-0 rounded-lg bg-white/85 px-2.5 py-2 text-center text-[0.8125rem] text-neutral-500">
          {placeholder}
        </p>
      ) : null}
    </CampaignPhonePreview>
  );
}

export function TemplatePickerDetail({
  template,
  discovery,
  preview,
  values,
  missingSlotKeys,
  missingCount,
  localError,
  sendError,
  callingHint,
  sending,
  sendDisabled,
  showBack,
  phonePlacement,
  sender,
  onBack,
  onValueChange,
  onSlotBlur,
  onCancel,
  onSend,
}: {
  template: WhatsAppTemplateDto | null;
  /** Conversation's WhatsApp endpoint, for the phone-frame header. Null while unknown. */
  sender: WhatsAppSelectedPhoneNumber | null;
  discovery: TemplateSendDiscovery | null;
  preview: WhatsAppTemplatePresentation | null;
  values: Record<string, string>;
  /** Required slots to mark invalid (touched or after a send attempt). */
  missingSlotKeys: ReadonlySet<string>;
  /** All unfilled required slots, for the footer count. */
  missingCount: number;
  localError: string | null;
  sendError: string | null;
  callingHint: string | null;
  sending: boolean;
  sendDisabled: boolean;
  showBack: boolean;
  phonePlacement: TemplatePickerPhonePlacement;
  onBack: () => void;
  onValueChange: (slotKey: string, value: string) => void;
  onSlotBlur: (slotKey: string) => void;
  onCancel: () => void;
  onSend: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const inlinePhone = phonePlacement === "inline";
  const phone = (
    <TemplatePickerPhone
      template={template}
      discovery={discovery}
      preview={preview}
      valuesBySlotKey={new Map(Object.entries(values))}
      sender={sender}
      frameClassName={INLINE_PHONE_FRAME_CLASS}
    />
  );

  if (template == null) {
    const prompt = (
      <div className="flex flex-col items-center justify-center gap-1 px-5 py-6 text-center">
        <FileTextIcon size={22} className="mb-0.5 text-inbox-subtle" aria-hidden="true" />
        <p className="m-0 text-[0.8125rem] font-semibold text-inbox-strong">
          {t("inbox.templateSend.selectPromptTitle")}
        </p>
        <p className="m-0 max-w-xs text-[0.75rem] leading-snug text-inbox-muted">
          {t("inbox.templateSend.selectPrompt")}
        </p>
      </div>
    );
    return (
      <div className="@container/tpd flex min-h-0 min-w-0 flex-1 flex-col bg-surface">
        {inlinePhone ? (
          <div className={cn("min-h-0 flex-1 overflow-y-auto px-3.5 py-3", INBOX_SCROLLER_CLASS)}>
            <InlineSplit phone={phone}>{prompt}</InlineSplit>
          </div>
        ) : (
          <div className="grid flex-1 place-items-center">{prompt}</div>
        )}
      </div>
    );
  }

  const supported = discovery?.kind === "supported";
  const unsupportedNotice =
    discovery?.kind === "unsupported" ? (
      <p
        className="m-0 flex items-start gap-2 rounded-[0.5625rem] border border-inbox-calm-line bg-inbox-calm-surface px-2.5 py-2 text-[0.75rem] text-[#5f5640]"
        role="status"
      >
        <AlertIcon size={16} className="mt-px shrink-0 text-inbox-calm" aria-hidden="true" />
        <span>{t("inbox.templateSend.unsupportedType")}</span>
      </p>
    ) : null;
  const parameters =
    supported && discovery.slots.length > 0 ? (
      <div className="@container/tpp min-w-0">
        <h4 className="m-0 mb-[0.4375rem] flex items-center gap-1.5 text-[0.6875rem] font-semibold text-inbox-subtle">
          {t("inbox.templateSend.parameters")}
          <span className="grid h-4 min-w-4 place-items-center rounded-full bg-inbox-pill px-1 text-[0.625rem] text-[#4f625d] tabular-nums">
            {discovery.slots.length}
          </span>
        </h4>
        <div className="grid gap-x-2.5 gap-y-2 @min-[340px]/tpp:grid-cols-2">
          {discovery.slots.map((slot) => {
            const key = slotStorageKey(slot);
            const fieldId = `template-slot-${key.replaceAll(":", "-")}`;
            const invalid = missingSlotKeys.has(key);
            const filled = (values[key] ?? "").trim().length > 0;
            const errorId = `${fieldId}-error`;
            return (
              <div key={key} className="flex min-w-0 flex-col gap-[3px]">
                <span className="flex h-4 min-w-0 items-center gap-1.5">
                  <label
                    htmlFor={fieldId}
                    className="flex min-w-0 items-center gap-1.5 text-[0.6875rem] font-medium text-[#4c5f59]"
                  >
                    <span className="min-w-0 truncate">{slotLabel(t, slot)}</span>
                    <code
                      dir="ltr"
                      data-placeholder={filled ? "filled" : "missing"}
                      className={cn(
                        "shrink-0 rounded-[4px] px-1 font-mono text-[0.625rem] font-semibold leading-[0.9375rem] [unicode-bidi:isolate] transition-colors duration-150",
                        filled ? "bg-[#eef3f2] text-[#3f5a52]" : "bg-[#fdf0e3] text-[#8a4b12]",
                      )}
                    >
                      {`{{${slot.parameterKey}}}`}
                    </code>
                  </label>
                  {invalid ? (
                    <em
                      id={errorId}
                      className="ms-auto shrink-0 text-[0.65625rem] font-medium not-italic text-destructive"
                      role="alert"
                    >
                      {t("inbox.templateSend.requiredShort")}
                    </em>
                  ) : null}
                </span>
                <input
                  id={fieldId}
                  className={cn(
                    "h-8 min-w-0 rounded-lg border border-[#d6e2dc] bg-surface px-2.5 text-[0.8125rem] text-inbox-strong outline-none transition-[border-color,box-shadow,background-color] duration-150",
                    "focus:border-primary focus:shadow-[0_0_0_3px_rgb(23_126_103/0.12)]",
                    "aria-[invalid=true]:border-[#e4a59f] aria-[invalid=true]:bg-[#fffafa] aria-[invalid=true]:focus:border-destructive aria-[invalid=true]:focus:shadow-[0_0_0_3px_rgb(192_54_44/0.12)]",
                    slot.componentType === "BUTTON" && "[direction:ltr] [unicode-bidi:isolate]",
                  )}
                  dir={templateSlotInputDir(slot)}
                  value={values[key] ?? ""}
                  onChange={(event) => onValueChange(key, event.target.value)}
                  onBlur={() => onSlotBlur(key)}
                  disabled={sending}
                  aria-invalid={invalid}
                  aria-required="true"
                  aria-describedby={invalid ? errorId : undefined}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
              </div>
            );
          })}
        </div>
      </div>
    ) : supported ? (
      <p className="m-0 flex items-center gap-2 rounded-[0.5625rem] bg-[#f5f8f7] px-2.5 py-2 text-[0.75rem] text-inbox-muted">
        <CheckCircleIcon size={14} className="shrink-0 text-primary" aria-hidden="true" />
        <span>{t("inbox.templateSend.noParameters")}</span>
      </p>
    ) : null;

  const side = (
    <div className="grid min-w-0 content-start gap-2.5">
      {unsupportedNotice}
      {parameters}
    </div>
  );

  const footerError = localError ?? sendError ?? callingHint;
  const hint = sending || !supported || footerError
    ? null
    : missingCount > 0
      ? missingCount === 1
        ? t("inbox.templateSend.missingOne")
        : t("inbox.templateSend.missingMany", { count: missingCount })
      : null;
  const ready = !sending && supported && !footerError && missingCount === 0;

  return (
    <section
      className="@container/tpd flex min-h-0 min-w-0 flex-1 flex-col bg-surface"
      aria-label={t("inbox.templateSend.preview")}
    >
      <div className={cn("min-h-0 flex-1 overflow-y-auto px-3.5 pt-3 pb-3.5", INBOX_SCROLLER_CLASS)}>
        {showBack ? (
          <button
            type="button"
            className="mb-2 inline-flex h-7 items-center gap-1.5 rounded-md px-1.5 text-[0.75rem] font-medium text-inbox-muted transition-colors hover:bg-inbox-hover hover:text-inbox-strong disabled:opacity-60"
            onClick={onBack}
            disabled={sending}
          >
            <ArrowLeftIcon size={14} className="rtl:rotate-180" aria-hidden="true" />
            {t("inbox.templateSend.back")}
          </button>
        ) : null}
        <div className="flex min-w-0 items-center gap-1.5">
          <h3 className="m-0 min-w-0 truncate text-[0.84375rem] font-semibold text-inbox-strong">
            <bdi>{template.name}</bdi>
          </h3>
          <TemplateMetaBadges category={template.category} language={template.language} />
        </div>
        <div className="mt-2.5">
          {inlinePhone ? <InlineSplit phone={phone}>{side}</InlineSplit> : side}
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-1.5 border-t border-inbox-line-soft px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom,0px))]">
        {footerError ? (
          <p className="m-0 text-[0.75rem] text-destructive" role="alert">
            {footerError}
          </p>
        ) : null}
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "me-auto min-w-0 truncate text-[0.71875rem]",
              ready ? "font-medium text-primary" : "text-inbox-subtle",
              showBack && "hidden",
            )}
            aria-live="polite"
          >
            {ready ? t("inbox.templateSend.ready") : hint}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(footerButtonClass, showBack ? "h-10 flex-1" : "h-8")}
            onClick={onCancel}
            disabled={sending}
          >
            {t("inbox.templateSend.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            className={cn(footerButtonClass, "gap-1.5", showBack ? "h-10 flex-1" : "h-8")}
            onClick={onSend}
            disabled={sendDisabled}
            aria-disabled={sendDisabled}
          >
            <SendIcon size={15} className="rtl:-scale-x-100" aria-hidden="true" />
            {sending ? t("inbox.templateSend.sending") : t("inbox.templateSend.send")}
          </Button>
        </div>
        {showBack && (ready || hint) ? (
          <span
            className={cn(
              "text-center text-[0.71875rem]",
              ready ? "font-medium text-primary" : "text-inbox-subtle",
            )}
            aria-live="polite"
          >
            {ready ? t("inbox.templateSend.ready") : hint}
          </span>
        ) : null}
      </div>
    </section>
  );
}

/**
 * Phone beside the fields when the detail pane is at least 400px wide, above them
 * otherwise. The phone is never dropped.
 */
function InlineSplit({
  phone,
  children,
}: {
  phone: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="grid items-start gap-3 @min-[400px]/tpd:grid-cols-[minmax(0,1fr)_12rem]">
      <div className="grid content-center rounded-[0.875rem] border border-inbox-line-soft bg-gradient-to-b from-[#f3f6f5] to-[#ebf1ef] px-2.5 py-3 @min-[400px]/tpd:sticky @min-[400px]/tpd:top-0 @min-[400px]/tpd:col-start-2 @min-[400px]/tpd:row-start-1 @min-[400px]/tpd:p-2">
        {phone}
      </div>
      <div className="min-w-0 @min-[400px]/tpd:col-start-1 @min-[400px]/tpd:row-start-1">
        {children}
      </div>
    </div>
  );
}

function slotLabel(
  t: (key: string, options?: Record<string, string>) => string,
  slot: TemplateSendSlot,
): string {
  switch (slot.componentType) {
    case "HEADER":
      return t("inbox.templateSend.slotHeader");
    case "BODY":
      return t("inbox.templateSend.slotBody");
    case "BUTTON":
      return slot.buttonLabel?.trim()
        ? t("inbox.templateSend.slotButton", { name: slot.buttonLabel.trim() })
        : t("inbox.templateSend.slotUrlButton");
    default: {
      const _exhaustive: never = slot.componentType;
      return _exhaustive;
    }
  }
}
